import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Tick-to-add upsell offers.
 *
 * Owns the ticks and the per-offer variant picker (and the photo that follows it),
 * and exposes the chosen lines as `selectedItems`. It does NOT normally add to cart
 * itself: on the earbuds PDP the quantity-break component already intercepts submit
 * so a multi-unit bundle can be posted as several lines, and two components fighting
 * over one submit would double the add. Instead quantity-breaks reads `selectedItems`
 * off this element and posts everything together.
 *
 * The standalone path below only runs when no quantity-break component shares the
 * section, so the block still works if it is placed on an ordinary product template.
 *
 * It also runs the offer countdown, and the last-chance popup that asks when a
 * shopper adds to cart with nothing ticked. The popup never holds that add up: it
 * opens on top of an add that is already on its way, and only posts the extra lines
 * the shopper takes from it. Whatever the answer, the cart opens with it — see
 * #onPopupClose.
 *
 * @typedef {object} UpsellChecksRefs
 * @property {HTMLInputElement[]} checks - One checkbox per offer.
 *
 * @extends {Component<UpsellChecksRefs>}
 */
class UpsellChecksComponent extends Component {
  requiredRefs = ['checks'];

  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {number | null} */
  #tick = null;

  /**
   * The add the open popup is riding on. `promise` settles true once it is in the
   * cart; `state` says how far it has got, which a promise cannot be asked.
   * @type {{ promise: Promise<boolean>, state: 'pending' | 'added' | 'refused' } | null}
   */
  #pendingAdd = null;

  /**
   * Cart drawers whose auto-open is on hold while the popup is up.
   * @type {Element[]}
   */
  #heldDrawers = [];

  /** True while the popup's own lines are being posted, so that add is not asked about. */
  #addingExtras = false;

  /** Whether the popup has been shown this visit; the fallback for when sessionStorage is unavailable. */
  #asked = false;

  /**
   * Per offer photo: the source the page rendered it with, kept so an untick can put
   * it back, and the variant photo it is meant to be showing ('' for the product's own).
   * @type {WeakMap<HTMLImageElement, { src: string, srcset: string | null, wanted: string }>}
   */
  #offerMedia = new WeakMap();

  /**
   * Variant photos fetched so far, by URL. Held on to, so each is fetched once and
   * stays ready to be shown.
   * @type {Map<string, HTMLImageElement>}
   */
  #variantMedia = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    // The popup's pickers are covered by its own change listener; see #bindPopup.
    for (const select of this.querySelectorAll('.upsell-checks__select')) {
      select.addEventListener('change', () => this.#syncRows(), { signal });
    }

    if (!this.#partnerComponent) {
      for (const form of this.#productForms) {
        form.addEventListener('submit', this.#onSubmit, { signal });
      }
    }

    this.#bindPopup(signal);
    this.#syncRows();
    this.#startTimer();

    // The photo a tick swaps to, fetched once the page itself is done.
    const warm = () => {
      for (const select of this.querySelectorAll('.upsell-checks__select, .upsell-popup__select')) {
        if (select instanceof HTMLSelectElement) this.#warmMedia(select.selectedOptions);
      }
    };
    if (document.readyState === 'complete') warm();
    else window.addEventListener('load', warm, { once: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#listeners = null;
    this.#stopTimer();
    // The close listener went with the signal, so nothing is posted or opened from a
    // component that is going away; the add the shopper asked for is already in.
    this.#pendingAdd = null;
    for (const drawer of this.#releaseDrawers()) drawer.removeAttribute('data-upsell-busy');
    if (this.#popup?.open) this.#popup.close();
    this.#releaseSubmit();
  }

  /**
   * Declarative `on:change` handler for the offer checkboxes.
   * @param {Event} event
   */
  toggleOffer(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#syncRows();
  }

  /**
   * The chosen offers, as cart line payloads.
   * Read by quantity-breaks.js as well as by this component's own submit path.
   * @returns {Array<{id: string, quantity: number}>}
   */
  get selectedItems() {
    /** @type {Array<{id: string, quantity: number}>} */
    const items = [];

    // The offers are withdrawn; nothing here is on offer for now.
    if (this.hidden) return items;

    for (const row of this.querySelectorAll('.upsell-checks__row')) {
      const check = row.querySelector('.upsell-checks__input');
      if (!(check instanceof HTMLInputElement) || !check.checked) continue;

      // A multi-variant offer is bought as whatever the select says, not as the
      // checkbox's own value — that only seeds the single-variant case.
      const select = row.querySelector('.upsell-checks__select');
      const id = select instanceof HTMLSelectElement ? select.value : check.value;
      if (id) items.push({ id, quantity: 1 });
    }

    return items;
  }

  /** Reveals each ticked offer's variant picker and that variant's photo, then tells the page. */
  #syncRows() {
    for (const row of this.querySelectorAll('.upsell-checks__row')) {
      const check = row.querySelector('.upsell-checks__input');
      const pick = row.querySelector('.upsell-checks__pick');
      if (pick instanceof HTMLElement && check instanceof HTMLInputElement) {
        pick.hidden = !check.checked;
      }
      this.#syncMedia(row, check instanceof HTMLInputElement && check.checked);
    }

    // Standalone only: with no offer ticked there is nothing to add, so hand submit
    // back and let the theme's own path run untouched.
    if (!this.#partnerComponent) this.#claimSubmit(this.selectedItems.length > 0);

    this.#announce();
  }

  /**
   * Lets quantity-breaks re-decide whether it needs to own the submit: with no tier
   * above 1 selected it hands submit back to the theme, and a ticked offer has to
   * pull it back.
   */
  #announce() {
    this.dispatchEvent(new CustomEvent('upsell:change', { bubbles: true }));
  }

  // --- variant photos -------------------------------------------------------

  /**
   * Shows the photo of the variant an offer's picker is on, for as long as the offer
   * is ticked. Unticked, the picker is hidden and the product's own photo is back —
   * as it is for a variant that has no photo of its own. An offer with no picker is
   * left alone.
   * @param {Element} offer - A row in the block, or its copy in the popup.
   * @param {boolean} ticked
   */
  #syncMedia(offer, ticked) {
    const image = offer.querySelector('img.upsell-checks__media');
    const select = offer.querySelector('select');
    if (!(image instanceof HTMLImageElement) || !(select instanceof HTMLSelectElement)) return;

    const state = this.#offerMedia.get(image) ?? {
      src: image.getAttribute('src') ?? '',
      srcset: image.getAttribute('srcset'),
      wanted: '',
    };
    this.#offerMedia.set(image, state);

    /**
     * @param {string} src
     * @param {string | null} srcset
     */
    const show = (src, srcset) => {
      if (image.getAttribute('src') === src && image.getAttribute('srcset') === srcset) return;
      if (srcset == null) image.removeAttribute('srcset');
      else image.setAttribute('srcset', srcset);
      image.setAttribute('src', src);
    };

    const media = (ticked && select.selectedOptions[0]?.dataset.media) || '';
    state.wanted = media;
    if (!media) {
      show(state.src, state.srcset);
      return;
    }

    // The photo only goes into the row once it has arrived; until then the one that
    // is there stays, where a bare swap can leave an empty frame while it downloads.
    // A variant photo is one file for every screen density, hence no srcset.
    const photo = this.#fetchMedia(media, 'high');
    const swap = () => {
      // A slow photo can land after the shopper has moved on to another model.
      if (state.wanted === media) show(media, null);
    };
    if (photo.complete && photo.naturalWidth > 0) swap();
    else photo.addEventListener('load', swap, { once: true });

    // The rest of the picker's photos, so that changing it is instant.
    this.#warmMedia(select.options);
  }

  /**
   * Fetches variant photos ahead of their being picked.
   * @param {Iterable<HTMLOptionElement>} options
   */
  #warmMedia(options) {
    for (const option of options) {
      if (option.dataset.media) this.#fetchMedia(option.dataset.media, 'low');
    }
  }

  /**
   * @param {string} url
   * @param {'high' | 'low'} priority - For a photo that has not been asked for yet.
   * @returns {HTMLImageElement} An off-page image that is fetching, or holding, that photo.
   */
  #fetchMedia(url, priority) {
    let photo = this.#variantMedia.get(url);
    if (!photo) {
      photo = new Image();
      photo.fetchPriority = priority;
      // Forgotten on failure, so that picking the model again retries it.
      photo.addEventListener('error', () => this.#variantMedia.delete(url), { once: true });
      photo.src = url;
      this.#variantMedia.set(url, photo);
    }
    return photo;
  }

  // --- offer countdown ------------------------------------------------------

  /**
   * Counts the visitor's offer window down in the heading. When it closes the next
   * one starts and the offers stay — unless the block sets a comeback period, in
   * which case they are withdrawn for that long. Does nothing unless the block
   * rendered a timer.
   */
  #startTimer() {
    // One in the block's heading and, when the popup is on, one inside it.
    const timers = this.querySelectorAll('.upsell-checks__timer');
    const values = this.querySelectorAll('.upsell-checks__timer-value');
    const minutes = Number(this.dataset.timerMinutes);
    if (!timers.length || !values.length || !(minutes > 0)) return;

    const first = this.#resolveDeadline(minutes);
    if (first == null) {
      this.#endOffer();
      return;
    }
    let deadline = first;

    const render = () => {
      let seconds = Math.ceil((deadline - Date.now()) / 1000);
      if (seconds <= 0) {
        // The window has closed. A comeback period withdraws the offers; without
        // one the next window starts here and they stay.
        const next = this.#comeback > 0 ? null : this.#resolveDeadline(minutes);
        if (next == null) {
          this.#endOffer();
          return;
        }
        deadline = next;
        seconds = Math.ceil((deadline - Date.now()) / 1000);
      }
      const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
      const ss = String(seconds % 60).padStart(2, '0');
      for (const value of values) value.textContent = `${mm}:${ss}`;
    };

    this.hidden = false;
    for (const timer of timers) if (timer instanceof HTMLElement) timer.hidden = false;
    render();
    this.#stopTimer();
    this.#tick = window.setInterval(render, 1000);
  }

  #stopTimer() {
    if (this.#tick != null) window.clearInterval(this.#tick);
    this.#tick = null;
  }

  /** How long the offers stay withdrawn once a window closes, in ms. 0: they never are. */
  get #comeback() {
    return (Number(this.dataset.timerComebackHours) || 0) * 3_600_000;
  }

  /**
   * When this visitor's window closes, starting a new one if the last has closed —
   * or null while a comeback period is still running, which is when the offers are
   * withdrawn. The inline script in blocks/upsell-checks.liquid makes the same
   * "withdrawn" call before paint and has to agree with this.
   * @param {number} minutes
   * @returns {number | null}
   */
  #resolveDeadline(minutes) {
    const now = Date.now();
    const fresh = now + minutes * 60_000;

    // The editor always shows a running timer, so the block can be styled.
    if (/** @type {any} */ (window).Shopify?.designMode) return fresh;

    const key = this.dataset.timerKey ?? '';

    try {
      const stored = Number(localStorage.getItem(key));
      // Capped at a fresh window so shortening the setting takes effect for everyone.
      if (stored > now) return Math.min(stored, fresh);
      if (stored && now < stored + this.#comeback) return null;
      localStorage.setItem(key, String(fresh));
    } catch {
      // No storage (private mode, blocked): the window lasts for this page view.
    }

    return fresh;
  }

  /** Unticks and hides the offers, and hands the submit back. */
  #endOffer() {
    this.#stopTimer();
    // Closing counts as a no: the cart opens with what was already added.
    this.#closePopup();
    for (const check of this.querySelectorAll('.upsell-checks__input')) {
      if (check instanceof HTMLInputElement) check.checked = false;
    }
    this.hidden = true;
    this.#syncRows();
  }

  // --- last-chance popup ----------------------------------------------------

  /** @returns {HTMLDialogElement | null} */
  get #popup() {
    const popup = this.querySelector('.upsell-popup');
    return popup instanceof HTMLDialogElement ? popup : null;
  }

  get #popupKey() {
    return `upsell-checks:popup:${this.dataset.productId ?? ''}`;
  }

  /** @returns {boolean} True once the popup has been shown in this visit. */
  #wasAsked() {
    if (this.#asked) return true;
    try {
      return sessionStorage.getItem(this.#popupKey) != null;
    } catch {
      return false;
    }
  }

  /**
   * Wires the popup's controls, and the listener that notices an add to cart.
   *
   * The popup rides on the theme's own cart event rather than on the button: by the
   * time that event exists the add request is already leaving, so the shopper's add
   * is never waiting on an answer. The listener sits on `window` in the capture
   * phase because it has to run before the cart drawer's listener on `document` —
   * that is where the drawer decides whether to open over the popup.
   * @param {AbortSignal} signal
   */
  #bindPopup(signal) {
    const popup = this.#popup;
    if (!popup) return;

    window.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartAdd, { capture: true, signal });

    popup.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest('.upsell-popup__accept')) this.#closePopup('accept');
        // The panel fills the dialog, so a click on the dialog itself is the backdrop.
        else if (target === popup || target.closest('.upsell-popup__close, .upsell-popup__decline')) this.#closePopup();
      },
      { signal }
    );
    popup.addEventListener('change', () => this.#syncPopup(), { signal });
    // Only Escape gets here with anything left to do; see #closePopup.
    popup.addEventListener('close', () => void this.#onPopupClose(), { signal });

    // The offer photos are lazy and sit in a closed dialog, so they would only start
    // downloading as the popup opens. Fetch them once the page itself is done.
    const warm = () => {
      for (const image of popup.querySelectorAll('img[loading="lazy"]')) {
        if (image instanceof HTMLImageElement) image.loading = 'eager';
      }
    };
    if (document.readyState === 'complete') warm();
    else window.addEventListener('load', warm, { once: true, signal });
  }

  /** @param {EventTarget | null} target */
  #ownsTarget(target) {
    return target instanceof Node && this.#productForms.some((productForm) => productForm.contains(target));
  }

  /** @returns {boolean} True when this add to cart should be followed by the popup. */
  #shouldAsk() {
    const popup = this.#popup;
    if (!popup || popup.open || this.#addingExtras || this.hidden) return false;
    if (this.selectedItems.length > 0) return false;

    // The editor asks every time, so the popup can be seen and styled.
    if (/** @type {any} */ (window).Shopify?.designMode) return true;

    // Every add with nothing ticked gets the popup, so a merchant checking their own
    // page always sees it. `data-popup-once` caps it at the first one of the visit.
    return !this.hasAttribute('data-popup-once') || !this.#wasAsked();
  }

  /**
   * An add to cart from this product's form has just started. Shows the popup over
   * it, and keeps the cart drawer shut until the shopper has answered.
   * @param {Event} event
   */
  #onCartAdd = (event) => {
    const popup = this.#popup;
    const { action, promise } = /** @type {any} */ (event);
    if (!popup || action !== 'add' || !this.#ownsTarget(event.target) || !this.#shouldAsk()) return;

    this.#asked = true;
    try {
      sessionStorage.setItem(this.#popupKey, '1');
    } catch {
      // No storage: #asked still holds it for this page view.
    }

    // `auto-open` is the drawer's own switch, read as it handles this very event.
    this.#heldDrawers = Array.from(document.querySelectorAll('cart-drawer-component[auto-open]'));
    for (const drawer of this.#heldDrawers) drawer.removeAttribute('auto-open');

    /** @type {{ promise: Promise<boolean>, state: 'pending' | 'added' | 'refused' }} */
    const pending = {
      promise: Promise.resolve(promise).then(
        (result) => !result?.detail?.didError,
        () => false
      ),
      state: 'pending',
    };
    this.#pendingAdd = pending;
    void pending.promise.then((ok) => {
      pending.state = ok ? 'added' : 'refused';
      // A refused add says why next to the button, which the popup is covering.
      if (!ok && this.#pendingAdd === pending) this.#closePopup();
    });

    for (const check of popup.querySelectorAll('.upsell-popup__input')) {
      if (check instanceof HTMLInputElement) check.checked = false;
    }
    this.#syncPopup();

    popup.returnValue = '';
    popup.showModal();
  };

  /**
   * Closes the popup and acts on the answer in the same breath. The dialog's `close`
   * event is delivered with the next frame, which is a visible beat before the cart
   * opens, so it is not waited for; when it does arrive there is nothing left to do.
   * @param {string} [answer] - 'accept' when the shopper took an offer.
   */
  #closePopup(answer = '') {
    const popup = this.#popup;
    if (!popup?.open) return;
    popup.close(answer);
    void this.#onPopupClose();
  }

  /**
   * The shopper has answered. Their add is already in (or on its way), so a no just
   * opens the cart, and a yes posts the picked offers as lines of their own.
   *
   * Either way the cart opens with the answer, never after it: a shopper who taps a
   * button and sees nothing happen reads the wait as the tap not having worked. When
   * the drawer has to open ahead of a request it says so (see `data-upsell-busy` in
   * blocks/upsell-checks.liquid) rather than showing a cart that is not true yet.
   */
  async #onPopupClose() {
    const popup = this.#popup;
    if (!popup) return;

    const extras = popup.returnValue === 'accept' ? this.#popupItems() : [];
    popup.returnValue = '';

    const pending = this.#pendingAdd;
    this.#pendingAdd = null;
    const drawers = this.#releaseDrawers();
    if (!pending) return;

    // Nothing went in: the theme is saying why next to the button, so stay out of the way.
    if (pending.state === 'refused') return;

    this.#setDrawersBusy(drawers, pending.state === 'pending' || extras.length > 0);
    this.#openDrawers(drawers);

    if (!(await pending.promise)) {
      // Refused after all, with the drawer already open on a spinner: an empty cart
      // over the theme's message helps nobody.
      this.#setDrawersBusy(drawers, false);
      for (const drawer of drawers) {
        if (!drawer.querySelector('.cart-form')) /** @type {any} */ (drawer.closest('theme-drawer'))?.close?.();
      }
      return;
    }

    try {
      if (extras.length && this.isConnected) await this.#addItems(extras, { quiet: true });
    } finally {
      this.#settleDrawers(drawers);
    }
  }

  /** Gives the cart drawers their auto-open back, and returns them. */
  #releaseDrawers() {
    const drawers = this.#heldDrawers;
    this.#heldDrawers = [];
    for (const drawer of drawers) drawer.setAttribute('auto-open', '');
    return drawers;
  }

  /** @param {Element[]} drawers */
  #openDrawers(drawers) {
    for (const drawer of drawers) {
      const themeDrawer = /** @type {any} */ (drawer.closest('theme-drawer'));
      if (themeDrawer && !themeDrawer.isOpen) themeDrawer.open?.();
    }
  }

  /**
   * Flags the drawers as waiting on a cart request. The attribute sits on
   * cart-drawer-component, which the cart's own re-render leaves alone.
   * @param {Element[]} drawers
   * @param {boolean} busy
   */
  #setDrawersBusy(drawers, busy) {
    for (const drawer of drawers) drawer.toggleAttribute('data-upsell-busy', busy);
  }

  /**
   * Clears the busy flag once the cart shows what was added. The theme swaps the
   * rows in a frame or two after its request resolves; letting go before that would
   * flash the empty-bag message in between.
   * @param {Element[]} drawers
   */
  #settleDrawers(drawers) {
    for (const drawer of drawers) {
      if (!drawer.hasAttribute('data-upsell-busy')) continue;
      if (drawer.querySelector('.cart-form')) {
        drawer.removeAttribute('data-upsell-busy');
        continue;
      }

      const done = () => {
        observer.disconnect();
        window.clearTimeout(giveUp);
        drawer.removeAttribute('data-upsell-busy');
      };
      const observer = new MutationObserver(() => {
        if (drawer.querySelector('.cart-form')) done();
      });
      const giveUp = window.setTimeout(done, 3000);
      observer.observe(drawer, { childList: true, subtree: true });
    }
  }

  /** Reveals each ticked offer's variant picker and that variant's photo; the accept button needs a tick. */
  #syncPopup() {
    const popup = this.#popup;
    if (!popup) return;

    let ticked = 0;
    for (const offer of popup.querySelectorAll('.upsell-popup__offer')) {
      const check = offer.querySelector('.upsell-popup__input');
      const pick = offer.querySelector('.upsell-popup__pick');
      const on = check instanceof HTMLInputElement && check.checked;
      if (pick instanceof HTMLElement) pick.hidden = !on;
      this.#syncMedia(offer, on);
      if (on) ticked++;
    }

    const accept = popup.querySelector('.upsell-popup__accept');
    if (accept instanceof HTMLButtonElement) accept.disabled = ticked === 0;
  }

  /**
   * The offers ticked in the popup, as cart line payloads. The block's real rows are
   * left unticked: these lines are posted here, and a tick would add them again with
   * the next add to cart.
   * @returns {Array<{id: string, quantity: number}>}
   */
  #popupItems() {
    /** @type {Array<{id: string, quantity: number}>} */
    const items = [];
    const popup = this.#popup;
    if (!popup) return items;

    for (const offer of popup.querySelectorAll('.upsell-popup__offer')) {
      if (!(offer instanceof HTMLElement)) continue;
      const picked = offer.querySelector('.upsell-popup__input');
      if (!(picked instanceof HTMLInputElement) || !picked.checked) continue;

      // A single-variant offer has no select; its id lives on the real row's checkbox.
      const select = offer.querySelector('.upsell-popup__select');
      const check = this.querySelector(
        `.upsell-checks__row[data-offer-index="${offer.dataset.offerIndex}"] .upsell-checks__input`
      );
      const id =
        select instanceof HTMLSelectElement ? select.value : check instanceof HTMLInputElement ? check.value : '';
      if (id) items.push({ id, quantity: 1 });
    }

    return items;
  }

  get #scope() {
    return this.closest('.shopify-section') ?? document;
  }

  /** @returns {Element | null} */
  get #partnerComponent() {
    return this.#scope.querySelector('quantity-breaks-component');
  }

  /** @returns {HTMLElement[]} */
  get #productForms() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    if (!productId) return [];
    const selector = `product-form-component[data-product-id="${productId}"]${
      sectionId ? `[data-section-id="${sectionId}"]` : ''
    }`;
    return /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll(selector)));
  }

  // --- adding lines (standalone submit, and the popup's picks) ---------------

  /**
   * Same `on:submit` removal trick quantity-breaks uses — the theme routes `on:`
   * handlers from a capture listener on `document`, so stopPropagation can never
   * beat it; dropping the attribute makes its lookup miss instead.
   */
  #claimSubmit(claim) {
    for (const form of this.#productForms) {
      if (claim) {
        if (form.hasAttribute('on:submit')) {
          form.dataset.upsellSubmit = form.getAttribute('on:submit') ?? '/handleSubmit';
          form.removeAttribute('on:submit');
        }
      } else if (form.dataset.upsellSubmit != null) {
        form.setAttribute('on:submit', form.dataset.upsellSubmit);
        delete form.dataset.upsellSubmit;
      }
    }
  }

  #releaseSubmit() {
    this.#claimSubmit(false);
  }

  /** @param {Event} event */
  #onSubmit = (event) => {
    const extras = this.selectedItems;
    if (!extras.length) return;

    event.preventDefault();
    event.stopPropagation();

    const [form] = this.#productForms;
    const main = form?.querySelector('input[name="id"]');
    const quantityInput = form?.querySelector('input[name="quantity"]');
    if (!(main instanceof HTMLInputElement) || !main.value) return;

    const items = [
      { id: main.value, quantity: Number(quantityInput?.value) || 1 },
      ...extras,
    ];

    void this.#addItems(items);
  };

  /**
   * @param {Array<{id: string, quantity: number}>} items
   * @param {object} [options]
   * @param {boolean} [options.quiet] - Skip the button's "added" animation; it has
   *   already played for the add these lines follow.
   */
  async #addItems(items, { quiet = false } = {}) {
    const [form] = this.#productForms;
    const buttons = Array.from(document.querySelectorAll('product-form-component add-to-cart-component'));
    const cartSections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((node) => (node instanceof HTMLElement ? node.dataset.sectionId : null))
      .filter(Boolean);

    for (const button of buttons) /** @type {any} */ (button).disable?.();

    const deferred = CartLinesUpdateEvent.createPromise();
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    this.#addingExtras = true;
    try {
      (form ?? this).dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
          promise: deferred.promise,
        })
      );
    } finally {
      this.#addingExtras = false;
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => ({ id: Number(item.id), quantity: item.quantity })),
          sections: cartSections.join(','),
        }),
      }).then((res) => res.json());

      if (response?.status) throw new Error(response.message || 'Add to cart failed');

      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      }).then((res) => res.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'upsell-checks-component',
          sourceId: this.dataset.productId ?? '',
          itemCount,
          productId: this.dataset.productId,
          // The cart markup came back with the add; without it the cart re-fetches it.
          sections: response.sections,
          didError: false,
        },
      });

      if (!quiet) for (const button of buttons) /** @type {any} */ (button).animateAddToCart?.();
    } catch (error) {
      console.error('[upsell-checks]', error);
      deferred.reject(error);
    } finally {
      for (const button of buttons) /** @type {any} */ (button).enable?.();
    }
  }
}

if (!customElements.get('upsell-checks-component')) {
  customElements.define('upsell-checks-component', UpsellChecksComponent);
}
